import React, { useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  exportProjectTasks,
  importTasks,
  validateTaskImport
} from '../api';
import './AccountView.css';

const getRoleLabel = (role) => {
  const labels = {
    owner: 'Propietario',
    admin: 'Administrador',
    collaborator: 'Colaborador',
    guest: 'Invitado'
  };

  return labels[role] || role || 'Sin rol';
};

function AccountView({
  session,
  projects = [],
  selectedProjectId,
  onBack,
  onLogout,
  onOpenFullView,
  onTasksImported
}) {
  const workspaceName =
    session?.workspace?.name || 'Workspace actual';

  const role =
    session?.workspace?.role ||
    session?.role ||
    session?.user?.role;

  const [activeSection, setActiveSection] = useState(null);
  const importExportOpen = activeSection === 'import-export';

  const importFileRef = useRef(null);
  const [importStatus, setImportStatus] = useState('idle');
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importFileName, setImportFileName] = useState('');

  const resetImport = () => {
    setImportStatus('idle');
    setImportRows([]);
    setImportErrors([]);
    setImportFileName('');

    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  };

  const handleExportProject = async () => {
    if (
      !selectedProjectId ||
      selectedProjectId === 'all' ||
      selectedProjectId === 'choose'
    ) {
      window.alert(
        'Selecciona un proyecto antes de exportar sus tareas.'
      );
      return;
    }

    try {
      const selectedProject = projects.find(
        (project) =>
          String(project._id) === String(selectedProjectId)
      );

      const { blob } = await exportProjectTasks(
        selectedProjectId
      );

      const safeProjectName = String(
        selectedProject?.name || 'proyecto'
      )
        .trim()
        .replace(
          /[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]+/g,
          '_'
        );

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download =
        `apile_${safeProjectName}_tareas.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      window.alert(
        error.response?.data?.error ||
        'No pudimos exportar las tareas de este proyecto.'
      );
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setImportFileName(file.name);
    setImportErrors([]);
    setImportRows([]);
    setImportStatus('validating');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header.trim(),

      complete: async (result) => {
        if (result.errors.length > 0) {
          setImportErrors(
            result.errors.map((item) => ({
              message:
                item.message ||
                'No pudimos interpretar una fila del archivo.'
            }))
          );
          setImportStatus('error');
          return;
        }

        try {
          const rows = result.data;
          const validation = await validateTaskImport(rows);

          setImportRows(rows);
          setImportErrors(validation.errors || []);
          setImportStatus('ready');
        } catch (error) {
          const apiErrors = error.response?.data?.errors;

          setImportErrors(
            Array.isArray(apiErrors) && apiErrors.length > 0
              ? apiErrors
              : [{
                  message:
                    error.response?.data?.error ||
                    'No pudimos validar el archivo.'
                }]
          );

          setImportStatus('error');
        }
      },

      error: () => {
        setImportErrors([{
          message: 'No pudimos leer el archivo CSV.'
        }]);
        setImportStatus('error');
      }
    });
  };

  const handleConfirmImport = async () => {
    if (!importRows.length) return;

    setImportStatus('importing');

    try {
      const result = await importTasks(importRows);

      if (onTasksImported) {
        await onTasksImported();
      }

      setImportRows([]);
      setImportErrors([]);
      setImportStatus('success');

      if (result?.imported === 0) {
        setImportErrors([{
          message: 'No se importó ninguna tarea.'
        }]);
      }
    } catch (error) {
      const apiErrors = error.response?.data?.errors;

      setImportErrors(
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors
          : [{
              message:
                error.response?.data?.error ||
                'No pudimos completar la importación.'
            }]
      );

      setImportStatus('error');
    }
  };

  return (
    <main className="account-view">
      <header className="account-view-header">
        <button
          type="button"
          className="account-back-button"
          onClick={onBack}
          aria-label="Volver"
        >
          ←
        </button>

        <div>
          <span className="account-view-eyebrow">
            Centro de control
          </span>
          <h1>Mi cuenta</h1>
        </div>

        {onOpenFullView && (
          <button
            type="button"
            className="account-full-view-button"
            onClick={onOpenFullView}
            aria-label="Ver versión completa"
            title="Ver versión completa"
          >
            <span aria-hidden="true"></span>
          </button>
        )}
      </header>

      <section className="account-identity">
        <div className="account-identity-mark">
          {session?.user?.name?.charAt(0)?.toUpperCase() || 'A'}
        </div>

        <div className="account-identity-copy">
          <strong>{session?.user?.name}</strong>
          <span>{session?.user?.email}</span>
          <small>{getRoleLabel(role)}</small>
        </div>
      </section>

      <nav className="account-pile-stack">
        <button
          type="button"
          className="account-pile account-pile-purple"
          onClick={() =>
            setActiveSection(
              importExportOpen ? null : 'import-export'
            )
          }
        >
          <span>
            <strong>Importar / Exportar tareas</strong>
            <small>Descarga, carga o respalda tus tareas</small>
          </span>
          <span aria-hidden="true">
            {importExportOpen ? '⌄' : '›'}
          </span>
        </button>

        {importExportOpen && (
          <div className="account-submenu">
            <a
              className="account-pile account-submenu-pile"
              href="/templates/apile_plantilla_importacion_tareas.xlsx"
              download
            >
              <span>
                <strong>Descargar plantilla</strong>
                <small>Hoja de cálculo (.xlsx)</small>
              </span>
              <span aria-hidden="true">›</span>
            </a>

            <input
              ref={importFileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />

            {importStatus === 'idle' && (
              <>
                <button
                  type="button"
                  className="account-pile account-submenu-pile"
                  onClick={() => importFileRef.current?.click()}
                >
                  <span>
                    <strong>Importar tareas</strong>
                    <small>
                      Carga tareas principales desde un archivo CSV
                    </small>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>

                <button
                  type="button"
                  className="account-pile account-submenu-pile"
                  onClick={handleExportProject}
                >
                  <span>
                    <strong>Exportar este proyecto</strong>
                    <small>
                      Descarga las tareas del proyecto seleccionado
                    </small>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>

                <button
                  type="button"
                  className="account-submenu-back"
                  onClick={() => setActiveSection(null)}
                >
                  Volver
                </button>
              </>
            )}

            {importStatus === 'validating' && (
              <div className="account-import-status">
                <span className="account-import-spinner" />
                <strong>Validando archivo...</strong>
                <small>{importFileName}</small>
              </div>
            )}

            {importStatus === 'ready' && (
              <div className="account-import-status">
                <strong>
                  {importRows.length} tareas listas para importar
                </strong>
                <small>{importFileName}</small>

                <div className="account-import-actions">
                  <button
                    type="button"
                    className="account-import-cancel"
                    onClick={resetImport}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="account-import-confirm"
                    onClick={handleConfirmImport}
                  >
                    Importar
                  </button>
                </div>
              </div>
            )}

            {importStatus === 'importing' && (
              <div className="account-import-status">
                <span className="account-import-spinner" />
                <strong>
                  Importando {importRows.length} tareas...
                </strong>
                <small>No cierres esta ventana.</small>
              </div>
            )}

            {importStatus === 'success' && (
              <div className="account-import-status">
                <strong>Importación completada</strong>
                <small>
                  Las tareas ya fueron agregadas a este proyecto.
                </small>

                <div className="account-import-actions">
                  <button
                    type="button"
                    className="account-import-confirm"
                    onClick={resetImport}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}

            {importStatus === 'error' && (
              <div className="account-import-status">
                <strong>No pudimos importar el archivo</strong>

                <div className="account-import-errors">
                  {importErrors.map((item, index) => (
                    <small key={`${index}-${item.message}`}>
                      {item.message}
                    </small>
                  ))}
                </div>

                <div className="account-import-actions">
                  <button
                    type="button"
                    className="account-import-cancel"
                    onClick={resetImport}
                  >
                    Volver
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="account-pile account-pile-blue"
        >
          <span>
            <strong>Workspaces</strong>
            <small>{workspaceName}</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        <button
          type="button"
          className="account-pile account-pile-coral"
        >
          <span>
            <strong>Miembros e invitaciones</strong>
            <small>Gestiona personas y accesos</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        <button
          type="button"
          className="account-pile account-pile-yellow"
        >
          <span>
            <strong>Proyectos y permisos</strong>
            <small>Roles y niveles de acceso</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        <button
          type="button"
          className="account-pile account-pile-teal"
        >
          <span>
            <strong>Configuración</strong>
            <small>Preferencias generales de aPile</small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        <a
          className="account-pile account-pile-lilac account-help-link"
          href="/faq/"
        >
          <span>
            <strong>Ayuda</strong>
            <small>Consulta cómo utilizar tareas y proyectos</small>
          </span>
          <span aria-hidden="true">›</span>
        </a>

        <button
          type="button"
          className="account-logout-button"
          onClick={onLogout}
        >
          Cerrar sesión
        </button>
      </nav>
    </main>
  );
}

export default AccountView;
